import React, { useState, useEffect } from 'react';
import { Client } from '../db';
import { clientsAPI } from '../backend';
import { formatPhone } from '../utils/formatPhone';
import { Edit } from 'lucide-react';

interface CustomerViewProps {
  cart: any[];
  onBack: () => void;
  onCustomerSelected: (customer: Client) => void;
}

function CustomerView({ cart, onBack, onCustomerSelected }: CustomerViewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [formMode, setFormMode] = useState<'none' | 'new' | 'edit'>('none');
  const [clientEdit, setClientEdit] = useState<Client | null>(null);
  const [clientes, setClientes] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<Client, 'id'>>({ name: '', email: '', phone: '', address: '', type: 'Distribuidor' });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientsAPI.getAll();
      setClientes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredClientes = clientes.filter(cliente =>
    cliente.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="loading">Loading clients...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleEdit = (client: Client) => {
    setClientEdit(client);
    setForm({ name: client.name, email: client.email, phone: client.phone, address: client.address, type: client.type ?? 'Distribuidor' });
    setFormMode('edit');
  };

  const closeForm = () => {
    setFormMode('none');
    setClientEdit(null);
    setForm({ name: '', email: '', phone: '', address: '', type: 'Distribuidor' });
  };

  return (
    <div className="customer-view">
      <div className="customer-header">
        <button className="btn-secondary" onClick={onBack}>
          ← Atrás
        </button>
        <h1>Seleccionar Cliente</h1>
        <div className="cart-summary-mini">
          <span>{totalItems} artículos</span>
          <span>${totalPrice.toFixed(2)}</span>
        </div>
      </div>

      <div className="customer-content">
        <div className="search-section">
          <input
            type="text"
            placeholder="Buscar clientes por nombre o correo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button
            className="btn-primary"
            onClick={() => setFormMode('new')}
          >
            + Nuevo Cliente
          </button>
        </div>

        {formMode !== 'none' ? (
          <div className="client-form-container">
            <div className="client-form-card">
              <div className="form-header">
                <h2>{formMode === 'new' ? 'Nuevo Cliente' : 'Editar Cliente'}</h2>
              </div>

              <form className="client-form" onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const payload = { ...form, phone: formatPhone(form.phone) };
                  if (formMode === 'edit' && clientEdit) {
                    const updatedClient = await clientsAPI.update(clientEdit.id, payload);
                    await loadClients();
                    onCustomerSelected(updatedClient);
                  } else {
                    const newClient = await clientsAPI.create(payload);
                    await loadClients();
                    onCustomerSelected(newClient);
                  }
                  closeForm();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Error saving client');
                }
              }}>
                <div className="form-group">
                  <label htmlFor="name" className="required">Nombre completo</label>
                  <input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ingresa el nombre completo"
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Correo electrónico</label>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="ejemplo@email.com"
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Teléfono</label>
                  <input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+1 234-567-8900"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address" className="required">Dirección</label>
                  <input
                    id="address"
                    type="text"
                    value={form.address}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="Calle 123, Ciudad"
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="type" className="required">Tipo</label>
                  <select
                    id="type"
                    value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value as 'Distribuidor' | 'Persona' }))}
                    className="form-input"
                    required
                  >
                    <option value="Distribuidor">Distribuidor</option>
                    <option value="Persona">Persona</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <button className="btn-secondary" type="button" onClick={closeForm}>
                    Cancelar
                  </button>
                  <button className="btn-primary" type="submit">
                    {formMode === 'new' ? 'Crear Cliente' : 'Guardar Cambios'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="customers-list">
            {filteredClientes.map(cliente => (
              <div
                key={cliente.id}
                className="customer-card"
                onClick={() => onCustomerSelected(cliente)}
              >
                <div className="customer-info">
                  <h3 className="client-item name">{cliente.name}</h3>
                  <p className="client-item">{cliente.email}</p>
                  <p className="client-item">{formatPhone(cliente.phone)}</p>
                </div>
                <div className="customer-actions">
                  <button
                    className="btn-secondary btn-small"
                    title="Editar cliente"
                    onClick={(e) => { e.stopPropagation(); handleEdit(cliente); }}
                  >
                    <Edit size={16} />
                  </button>
                  <div className="customer-select">
                    <span>→</span>
                  </div>
                </div>
              </div>
            ))}
            {filteredClientes.length === 0 && searchTerm && (
              <div className="no-results">
                <p>No se encontraron clientes para "{searchTerm}"</p>
                <button
                  className="btn-secondary"
                  onClick={() => setFormMode('new')}
                >
                  Registrar nuevo cliente
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerView; 